import { weekdayKeys } from "../../lib/schedule-time/schedule-dates";
import Konva from "konva";
import type { CustomArt } from "../../stores/custom-art-store";
import { loadArtImage, scheduleDayArtSources } from "../../lib/schedule-art/art-assets";
import { loadArtFonts } from "../../lib/schedule-art/art-fonts";
import { renderArtText, type ArtData } from "./art-text";
import { loadPlatformImages } from "../../lib/schedule-art/platform-images";
import { ART_HEIGHT, ART_WIDTH, createArtExportStage, rasterizeArtNode } from "../../lib/schedule-art/art-canvas";

export const PIXELS_PER_INCH = 144;

export type PowerPointElement = {
  name: string;
  node: Konva.Shape | Konva.Group;
  text?: Konva.Text;
  fixed: boolean;
};

const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export async function createPowerPointScene(
  data: ArtData,
  layers: CustomArt[],
  offlineLabel: string,
) {
  const daySources = scheduleDayArtSources(data.preset, data.week, data.slots);
  const sources = [data.preset.base, ...daySources];
  const metadata = data.canvasMetadata ?? data.preset.metadata;
  const [images] = await Promise.all([
    Promise.all(
      sources.map((source) => source ? loadArtImage(source) : undefined),
    ),
    loadArtFonts(metadata),
    loadPlatformImages(),
  ]);
  const stage = createArtExportStage();

  try {
    const layer = new Konva.Layer({ listening: false });
    stage.add(layer);
    const elements: PowerPointElement[] = [];
    layers.filter(art => art.visible).forEach(art => {
      const node = new Konva.Image({ image: art.image, ...art.transform });
      layer.add(node);
      elements.push({ name: art.name, node, fixed: true });
    });
    images.forEach((image, index) => {
      if (!image) return;
      const node = new Konva.Image({
        image,
        x: 0,
        y: 0,
        width: ART_WIDTH,
        height: ART_HEIGHT,
      });
      layer.add(node);
      const name = index === 0 ? "Preset Base" : `${weekdays[index - 1]} Background`;
      elements.push({ name, node, fixed: true });
    });
    const group = new Konva.Group();
    layer.add(group);
    renderArtText(group, data, offlineLabel);
    group.getChildren().forEach((node) => {
      if (!(node instanceof Konva.Group)) return;
      const bind = String(node.getAttr("metadataBind") ?? "Element");
      const day = weekdayKeys.findIndex((key) => key === String(node.getAttr("metadataDay")));
      const name = day < 0 ? bind : `${weekdays[day]} - ${bind}`;
      const fixed =
        bind === "day.date" ||
        bind === "week.range" ||
        bind.startsWith("profile.");
      const text = node.findOne<Konva.Text>("Text");
      elements.push({ name, node, text, fixed });
    });
    return { stage, elements };
  } catch (error) {
    stage.destroy();
    throw error;
  }
}

export function rasterizePowerPointElement(
  stage: Konva.Stage,
  element: PowerPointElement,
) {
  const padding = element.text ? element.text.fontSize() * 0.3 : 0;
  const raster = rasterizeArtNode(stage, element.node, padding);
  return {
    data: raster.canvas.toDataURL("image/png"),
    x: raster.left / PIXELS_PER_INCH,
    y: raster.top / PIXELS_PER_INCH,
    w: (raster.right - raster.left) / PIXELS_PER_INCH,
    h: (raster.bottom - raster.top) / PIXELS_PER_INCH,
    objectName: element.name,
  };
}
